/**
 * Filtro origen multi + campañas Meta anidadas.
 * @module-isolated analytics-cohort
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CAMPAIGN_NONE,
  ORIGIN_META,
  originHint
} from '../utils/filter-cohort.js';

export default function OriginCampaignFilter({
  originOptions = [],
  originIds = [],
  onOriginChange,
  campaigns = [],
  campaignIds = [],
  onCampaignChange,
  allMetaCampaigns = true,
  onAllMetaCampaignsChange,
  disabled = false,
  loading = false
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const originSet = useMemo(() => new Set((originIds || []).map(String)), [originIds]);
  const campSet = useMemo(() => new Set((campaignIds || []).map(String)), [campaignIds]);
  const metaOn = originSet.has(ORIGIN_META);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary = useMemo(() => {
    if (loading) return 'Cargando…';
    if (!originIds.length) return 'Ninguno';
    return (
      originHint(
        { originIds: originIds, campaignIds: campaignIds, allMetaCampaigns: allMetaCampaigns },
        campaigns.length
      ) || 'Orígenes'
    );
  }, [loading, originIds, campaignIds, allMetaCampaigns, campaigns.length]);

  function setOrigins(next) {
    onOriginChange(Array.from(new Set(next.map(String))));
  }

  function toggleOrigin(oid) {
    const next = new Set(originSet);
    if (next.has(oid)) next.delete(oid);
    else next.add(oid);
    setOrigins(Array.from(next));
  }

  function toggleCampaign(cid) {
    const ids = allMetaCampaigns
      ? (campaigns || []).map((c) => c.id)
      : Array.from(campSet);
    const next = new Set(ids);
    if (next.has(cid)) next.delete(cid);
    else next.add(cid);
    if (onAllMetaCampaignsChange) onAllMetaCampaignsChange(false);
    onCampaignChange(Array.from(next));
  }

  function selectAllMetaCampaigns() {
    if (onAllMetaCampaignsChange) onAllMetaCampaignsChange(true);
    onCampaignChange((campaigns || []).map((c) => c.id));
    if (!metaOn) setOrigins(originIds.concat([ORIGIN_META]));
  }

  function selectMetaOnly() {
    setOrigins([ORIGIN_META]);
    selectAllMetaCampaigns();
  }

  function selectAll() {
    const ids = [ORIGIN_META].concat((originOptions || []).map((o) => o.id));
    setOrigins(ids);
    selectAllMetaCampaigns();
  }

  function clearAll() {
    setOrigins([]);
    onCampaignChange([]);
    if (onAllMetaCampaignsChange) onAllMetaCampaignsChange(false);
  }

  return (
    <div className="analytics-field analytics-origin-filter" ref={rootRef}>
      <span id={id + '-label'}>Origen</span>
      <button
        type="button"
        className={'analytics-origin-filter-trigger' + (open ? ' is-open' : '')}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={id + '-label'}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="analytics-origin-filter-summary">{summary}</span>
        <span className="analytics-origin-filter-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div className="analytics-origin-filter-panel" role="listbox" aria-multiselectable="true">
          <div className="analytics-origin-filter-actions">
            <button type="button" className="analytics-btn-link" onClick={selectMetaOnly}>
              Solo Meta
            </button>
            <button type="button" className="analytics-btn-link" onClick={selectAll}>
              Todas
            </button>
            <button type="button" className="analytics-btn-link" onClick={clearAll}>
              Limpiar
            </button>
          </div>

          <div className="analytics-origin-filter-group">
            <div className="analytics-origin-filter-group-label">Meta</div>
            <label className="analytics-origin-filter-option is-meta">
              <input
                type="checkbox"
                checked={metaOn}
                onChange={() => toggleOrigin(ORIGIN_META)}
              />
              <span>Meta</span>
            </label>
            <label
              className={
                'analytics-origin-filter-option analytics-cohort-camp-option' +
                (!metaOn ? ' is-disabled' : '')
              }
            >
              <input
                type="checkbox"
                checked={metaOn && allMetaCampaigns}
                disabled={!metaOn}
                onChange={() => {
                  if (allMetaCampaigns) {
                    if (onAllMetaCampaignsChange) onAllMetaCampaignsChange(false);
                    onCampaignChange((campaigns || []).map((c) => c.id));
                  } else {
                    selectAllMetaCampaigns();
                  }
                }}
              />
              <span>Todas las campañas Meta</span>
            </label>
            {(campaigns || []).map((c) => (
              <label
                key={c.id}
                className={
                  'analytics-origin-filter-option analytics-cohort-camp-option' +
                  (c.id === CAMPAIGN_NONE ? '' : ' is-meta') +
                  (!metaOn ? ' is-disabled' : '')
                }
              >
                <input
                  type="checkbox"
                  checked={
                    metaOn && (allMetaCampaigns || campSet.has(c.id))
                  }
                  disabled={!metaOn}
                  onChange={() => toggleCampaign(c.id)}
                />
                <span>
                  {c.label}
                  {c.count != null ? ' (' + c.count + ')' : ''}
                </span>
              </label>
            ))}
            {!campaigns.length ? (
              <p className="analytics-origin-filter-group-label">
                Sin campañas en este período
              </p>
            ) : null}
          </div>

          <div className="analytics-origin-filter-group">
            <div className="analytics-origin-filter-group-label">Otros orígenes</div>
            {(originOptions || []).map((o) => (
              <label
                key={o.id}
                className={
                  'analytics-origin-filter-option' + (o.isMeta === false ? ' is-other' : '')
                }
              >
                <input
                  type="checkbox"
                  checked={originSet.has(o.id)}
                  onChange={() => toggleOrigin(o.id)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
